import {
  VStack,
  HStack,
  Text,
  Divider,
  Button,
  Icon,
  Center,
  Flex,
  Spacer,
} from '@chakra-ui/react';

import { IoCreateOutline, IoRemoveCircleOutline } from 'react-icons/io5';

import sumBy from 'lodash/sumBy';
import formatPriceNumber from '~/utils/formatPriceNumber';

import { OrderItem, OrderOption } from '~/types';

import { useTranslation } from 'react-i18next';

type ItemProps = {
  item: OrderItem;
  allowEdit?: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
};

// 餐點品項資訊 包含名稱，選項，價錢 可控制是否可編輯
export default function ProductItem({ item, allowEdit = false, onDelete, onEdit }: ItemProps) {
  const { t } = useTranslation();
  const { count, name, note, price, modifiers } = item;

  const optionList = modifiers.reduce((acc: Array<OrderOption>, modifier) => {
    return acc.concat(modifier.options);
  }, []);

  const itemTotalPrice = (sumBy(optionList, ({ price }) => price) + price) * count;

  return (
    <VStack
      align="stretch"
      spacing={0}
      borderRadius="5px"
      borderWidth="1px"
      borderColor="wsGray.400"
    >
      <VStack align="stretch" px={2} pt={2}>
        <HStack
          align="flex-start"
          onClick={allowEdit ? onEdit : undefined}
          cursor={allowEdit ? 'pointer' : 'default'}
        >
          <Center borderRadius="5px" bg="wsGray.200" w="2rem" h="2rem">
            <Text>{count}</Text>
          </Center>

          <Text flex="1" py={1} fontWeight="bold" fontSize="sm">
            {name}
          </Text>
          {allowEdit && (
            <Center py={1}>
              <Icon as={IoCreateOutline} boxSize="1.2rem" />
            </Center>
          )}
        </HStack>

        {optionList.map(({ name, id, price }, index) => (
          <HStack key={`${id}_${index}`} align="center" justify="space-between" w="full">
            <Text color="wsGray.500" fontSize="xs">
              {name}
            </Text>
            {price !== 0 && (
              <Text color="wsGray.500" fontSize="xs">
                +{formatPriceNumber(price)}
              </Text>
            )}
          </HStack>
        ))}

        {note && (
          <Text color="wsGray.500" fontSize="xs">
            {note}
          </Text>
        )}
        <Divider />
      </VStack>

      <Flex>
        {allowEdit && (
          <Button
            py={5}
            pl={2}
            pr={4}
            bg="white"
            size="xs"
            variant="ghost"
            leftIcon={<Icon as={IoRemoveCircleOutline} boxSize="1.2rem" color="wsRed.600" />}
            iconSpacing={1}
            fontWeight="normal"
            color="wsGray.500"
            onClick={onDelete}
            letterSpacing={1}
            _hover={{ bg: 'wsRed.200' }}
            _active={{ bg: 'wsRed.200' }}
          >
            {t('store.order.removeItem')}
          </Button>
        )}
        <Spacer />
        <Text fontSize="xs" fontWeight="bold" px={2} py={2.5}>
          {formatPriceNumber(itemTotalPrice)}
        </Text>
      </Flex>
    </VStack>
  );
}
